import React from 'react';

import {
  exportAuditLogs,
  exportPlateHistoryReport,
  exportSampleContainerHistory,
  exportLocationHistoryReport,
} from 'audit/AuditActions';
import {
  EXPORT_AUDIT_LOG_DISPLAY_STRING,
  EXPORT_LOCATION_HISTORY_DISPLAY_STRING,
  EXPORT_PLATE_HISTORY_DISPLAY_STRING,
  EXPORT_SAMPLE_CONTAINER_HISTORY_DISPLAY_STRING,
} from 'audit/AuditConstants';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';

export interface StorableExportButtonProps {
  storableId: string;
  buttonStyle?: ButtonStyle;
}

export default function StorableExportButton({
  storableId,
  buttonStyle,
}: StorableExportButtonProps): JSX.Element {
  // This is shown in the storable's metadata tab and a grid view's sidebar
  const auditExportButtonStyle = buttonStyle || ButtonStyle.TERTIARY_BLUE;
  const canExportPlateHistory = APIUtil.isPlate(storableId);
  const canExportContainerHistory = APIUtil.isContainer(storableId);
  const canExportLocationHistory = APIUtil.isLocation(storableId);
  const showDropdown =
    licenses.hasEnterprisePlan() &&
    (canExportPlateHistory || canExportContainerHistory || canExportLocationHistory);

  return (
    <>
      {showDropdown ? (
        <Tooltip title="Export report">
          <Dropdown
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FIT_CONTENT} dropLeft={true}>
                {canExportContainerHistory && (
                  <Option
                    onSelect={() => {
                      exportSampleContainerHistory([storableId]);
                    }}
                    dataTestAttrs={{
                      component: 'StorableExport',
                      element: 'dropdownOption',
                      key: 'exportContainerHistoryReport',
                    }}
                  >
                    {EXPORT_SAMPLE_CONTAINER_HISTORY_DISPLAY_STRING}
                  </Option>
                )}
                {canExportPlateHistory && (
                  <Option
                    onSelect={() => {
                      exportPlateHistoryReport([storableId]);
                    }}
                    dataTestAttrs={{
                      component: 'StorableExport',
                      element: 'dropdownOption',
                      key: 'exportPlateHistoryReport',
                    }}
                  >
                    {EXPORT_PLATE_HISTORY_DISPLAY_STRING}
                  </Option>
                )}
                {canExportLocationHistory && (
                  <Option
                    onSelect={() => {
                      exportLocationHistoryReport([storableId]);
                    }}
                    dataTestAttrs={{
                      component: 'StorableExport',
                      element: 'dropdownOption',
                      key: 'exportLocationHistoryReport',
                    }}
                  >
                    {EXPORT_LOCATION_HISTORY_DISPLAY_STRING}
                  </Option>
                )}
                <Option
                  onSelect={() => {
                    exportAuditLogs([storableId]);
                  }}
                  dataTestAttrs={{
                    component: 'StorableExport',
                    element: 'dropdownOption',
                    key: 'exportAuditLog',
                  }}
                >
                  {EXPORT_AUDIT_LOG_DISPLAY_STRING}
                </Option>
              </Menu>
            }
          >
            <Button
              icon="share-v2"
              isDropdown={true}
              buttonStyle={auditExportButtonStyle}
              size={ButtonSize.SMALL}
              className="u-no-wrap u-mini-spacing-left"
              dataTestAttrs={{
                component: 'StorableExport',
                element: 'button',
                key: 'exportDropdownToggle',
              }}
            />
          </Dropdown>
        </Tooltip>
      ) : (
        <ExportAuditLogButton
          buttonStyle={auditExportButtonStyle}
          ids={[storableId]}
          className="u-mini-spacing-left"
        />
      )}
    </>
  );
}
